import {
  createDiagnosticOperationId,
  recordDiagnosticOutcome,
  withDiagnosticStep,
} from "@mytour/platform";
import {invalidatePageQueries} from "@mytour/platform/invalidatePageQueries";
import type {
  CertificatRamonageDetails,
  CertificatRamonagePayload,
  ICustomer,
  SaveCertificatRamonageResponse,
} from "@mytour/shared";
import {RAMONAGE_PDF_MAX_BYTES, ramonagePhotoInputSchema} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import {certificateApiService} from "./certificateApi.service";

export const certificateKeysStore = {
  root: ["certificatRamonage"] as const,
  certificate: (eventId: string) => [...certificateKeysStore.root, eventId] as const,
  latestByCustomer: (customerId: string) =>
    [...certificateKeysStore.root, "customer", customerId, "latest"] as const,
  documentsByCustomer: (customerId: string) =>
    [...certificateKeysStore.root, "customer", customerId, "documents"] as const,
};

export function useCertificate(
  eventId: string,
  enabled = true,
  refetchOnMount: boolean | "always" = true
) {
  const {data, isLoading, isFetching, error, refetch} = useQuery({
    queryKey: certificateKeysStore.certificate(eventId),
    queryFn: () => certificateApiService.getCertificate(eventId),
    enabled: enabled && !!eventId,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    certificate: data,
    isCertificateLoading: isLoading,
    isCertificateFetching: isFetching,
    certificateError: error,
    refetchCertificate: refetch,
  };
}

export function useLatestCertificateByCustomer(customerId: string, enabled = true) {
  const {data, isLoading} = useQuery({
    queryKey: certificateKeysStore.latestByCustomer(customerId),
    queryFn: () => certificateApiService.getLatestCertificateByCustomer(customerId),
    enabled: enabled && !!customerId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    latestCertificate: data,
    isLatestCertificateLoading: isLoading,
  };
}

export function useCertificateDocuments(customerId: string | undefined, enabled = true) {
  const {data, isLoading} = useQuery({
    queryKey: certificateKeysStore.documentsByCustomer(customerId as string),
    queryFn: () => certificateApiService.listDocumentsByCustomer(customerId as string),
    enabled: enabled && !!customerId,
    refetchOnWindowFocus: false,
    // Aligné sur `useCustomerDocuments` — la timeline Documents mêle les deux sources.
    staleTime: 2 * 60 * 1000,
  });

  return {
    certificateDocuments: data,
    isCertificateDocumentsLoading: isLoading,
  };
}

export function useResendCertificate() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (eventId: string) => certificateApiService.resend(eventId),
    onSuccess() {
      // Statut d'envoi lu à la fois par la fiche événement et par Documents —
      // invalider toute la racine plutôt que de traquer les deux clés individuellement. (#1270)
      queryClient.invalidateQueries({queryKey: certificateKeysStore.root});
    },
  });

  return {resendCertificate: mutateAsync, isResendingCertificate: isPending};
}

export interface CompleteCertificateVariables {
  operationId?: string;
  eventId: string;
  payload?: CertificatRamonagePayload;
  sendEmail: boolean;
  customer: ICustomer;
  generatePdf: (certificate: CertificatRamonageDetails) => Promise<Blob>;
  preparePayload?: (payload: CertificatRamonagePayload) => Promise<CertificatRamonagePayload>;
  onSaved?: () => void;
}

export function useCompleteCertificate() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation<
    SaveCertificatRamonageResponse,
    Error,
    CompleteCertificateVariables
  >({
    mutationFn: async (
      vars: CompleteCertificateVariables
    ): Promise<SaveCertificatRamonageResponse> => {
      const operationId = vars.operationId ?? createDiagnosticOperationId();
      let certificate = await withDiagnosticStep(operationId, "read", () =>
        certificateApiService.getCertificate(vars.eventId)
      );
      assertCurrentAttempt(certificate, vars.payload);
      if (!certificate) {
        const preparePayload = vars.preparePayload;
        const initialPayload = vars.payload;
        const payload =
          initialPayload && preparePayload
            ? await withDiagnosticStep(operationId, "generate", () =>
                preparePayload(initialPayload)
              )
            : vars.payload;
        try {
          certificate = await withDiagnosticStep(operationId, "save", async () => {
            if (!payload) {
              throw new Error("Certificate not found");
            }
            return certificateApiService.saveCertificate(vars.eventId, payload);
          });
        } catch (error) {
          if (isSignatureModelConflict(error)) {
            throw error;
          }
          let current: CertificatRamonageDetails | null = null;
          try {
            current = await withDiagnosticStep(operationId, "read", () =>
              certificateApiService.getCertificate(vars.eventId)
            );
          } catch {
            throw error;
          }
          if (!current) {
            throw error;
          }
          assertCurrentAttempt(current, vars.payload);
          certificate = current;
        }
      }
      queryClient.setQueryData(certificateKeysStore.certificate(vars.eventId), certificate);
      vars.onSaved?.();
      await Promise.all([
        invalidatePageQueries(queryClient, ["customer"]),
        invalidatePageQueries(queryClient, ["agendaEvent"]),
        queryClient.invalidateQueries({queryKey: ["late"]}),
        queryClient.invalidateQueries({queryKey: ["to-be-paid"]}),
      ]);
      if (certificate.hasFile) {
        const emailStatus =
          certificate.emailStatus === "bounced"
            ? "failed"
            : (certificate.emailStatus ?? "skipped");
        recordDiagnosticOutcome(operationId, "email", emailStatus);
        return {certificate, emailStatus};
      }
      const savedCertificate = certificate;
      for (const appareil of savedCertificate.appareils ?? []) {
        for (const photo of appareil.photos ?? []) {
          if (!ramonagePhotoInputSchema.safeParse(photo).success) {
            throw new Error(
              "Une photo sauvegardée est indisponible. La finalisation est impossible."
            );
          }
        }
      }
      const blob = await withDiagnosticStep(operationId, "generate", () =>
        vars.generatePdf(savedCertificate)
      );
      if (blob.size > RAMONAGE_PDF_MAX_BYTES) {
        throw new Error("Le PDF dépasse la taille maximale autorisée (6 Mo).");
      }
      let result: SaveCertificatRamonageResponse;
      try {
        result = await withDiagnosticStep(
          operationId,
          "upload",
          () =>
            certificateApiService.uploadFile(vars.eventId, blob, vars.sendEmail, vars.customer),
          {fileSize: blob.size, fileType: blob.type}
        );
      } catch (error) {
        let current: CertificatRamonageDetails | null = null;
        try {
          current = await withDiagnosticStep(operationId, "read", () =>
            certificateApiService.getCertificate(vars.eventId)
          );
        } catch {
          throw error;
        }
        if (!current?.hasFile) {
          throw error;
        }
        result = {
          certificate: current,
          emailStatus:
            current.emailStatus === "bounced" ? "failed" : (current.emailStatus ?? "skipped"),
        };
      }
      recordDiagnosticOutcome(operationId, "email", result.emailStatus);
      return result;
    },
    onSuccess(result, vars) {
      queryClient.setQueryData(certificateKeysStore.certificate(vars.eventId), result.certificate);
    },
    onSettled() {
      queryClient.invalidateQueries({queryKey: certificateKeysStore.root});
    },
    retry: false,
  });
  return {completeCertificate: mutateAsync, isCompletingCertificate: isPending};
}

function assertCurrentAttempt(
  certificate: CertificatRamonageDetails | null,
  payload: CertificatRamonagePayload | undefined
) {
  if (
    certificate &&
    payload?.clientAttemptId &&
    certificate.clientAttemptId !== payload.clientAttemptId
  ) {
    throw new Error("Une autre attestation existe déjà pour ce rendez-vous.");
  }
}

function isSignatureModelConflict(error: unknown) {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    error.response.data?.code === "SIGNATURE_MODEL_CHANGED"
  );
}
